import type { TimeOffSuggestion, TodayBrief } from '@hugo/core'
import { useQueryClient } from '@tanstack/react-query'
import { CalendarPlusIcon, ExternalLinkIcon, TreePalmIcon, XIcon } from 'lucide-react'
import { useId, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { queryKeys, type TimeOffList, useRunTool } from '@/lib/items'
import { timeOffText } from '@/lib/plan'
import { attention } from '@/lib/styles'
import { cn } from '@/lib/utils'
import { Parts } from './parts'

/** Full-width and tall enough for a thumb on phones; a normal button from `sm` up. */
const THUMB = 'h-10 w-full sm:h-8 sm:w-auto'

/**
 * Add a suggestion as time off (its days and label, through `time_off.add`) or dismiss it. It
 * leaves Settings and Today right away and comes back if the call fails; one already on its way
 * is not acted on twice.
 */
function useSuggestionActions() {
  const queryClient = useQueryClient()
  const { run } = useRunTool()
  const inflight = useRef(new Set<string>())

  return useMemo(() => {
    const settle = async <T,>(key: string, call: () => Promise<T | null>) => {
      if (inflight.current.has(key)) return null
      inflight.current.add(key)
      try {
        await queryClient.cancelQueries({ queryKey: queryKeys.timeOff })
        await queryClient.cancelQueries({ queryKey: queryKeys.today })
        const list = queryClient.getQueryData<TimeOffList>(queryKeys.timeOff)
        const brief = queryClient.getQueryData<TodayBrief>(queryKeys.today)
        queryClient.setQueryData<TimeOffList>(queryKeys.timeOff, (old) =>
          old ? { ...old, suggestions: old.suggestions.filter((s) => s.key !== key) } : old,
        )
        queryClient.setQueryData<TodayBrief>(queryKeys.today, (old) =>
          old
            ? { ...old, timeOffSuggestions: old.timeOffSuggestions.filter((s) => s.key !== key) }
            : old,
        )
        const result = await call()
        if (result === null) {
          if (list) queryClient.setQueryData(queryKeys.timeOff, list)
          if (brief) queryClient.setQueryData(queryKeys.today, brief)
        }
        return result
      } finally {
        inflight.current.delete(key)
      }
    }
    return {
      add: (suggestion: TimeOffSuggestion) =>
        settle(suggestion.key, () =>
          run(
            'time_off.add',
            { startsOn: suggestion.startsOn, endsOn: suggestion.endsOn, label: suggestion.label },
            { done: 'Time off added', failed: 'Could not add time off' },
          ),
        ),
      dismiss: (suggestion: TimeOffSuggestion) =>
        settle(suggestion.key, () =>
          run(
            'time_off.dismiss_suggestion',
            { key: suggestion.key },
            { done: 'Suggestion dismissed', failed: 'Could not dismiss the suggestion' },
          ),
        ),
    }
  }, [queryClient, run])
}

/**
 * Out-of-office time from Google Calendar that is not time off yet: the days ("Mon 26 Oct – Fri
 * 6 Nov · Vacation · 10 working days"), a link to the event, and "Add as time off" / "Dismiss".
 * `callout` (Today) explains why it matters; `row` (Settings) is compact.
 */
export function TimeOffSuggestionView({
  suggestion,
  thisYear,
  layout,
}: {
  suggestion: TimeOffSuggestion
  thisYear: number
  layout: 'callout' | 'row'
}) {
  const actions = useSuggestionActions()
  const titleId = useId()
  const [busy, setBusy] = useState<'add' | 'dismiss' | null>(null)
  const act = async (which: 'add' | 'dismiss') => {
    setBusy(which)
    await (which === 'add' ? actions.add(suggestion) : actions.dismiss(suggestion))
    setBusy(null)
  }
  const text = timeOffText(suggestion, thisYear)
  const link = suggestion.url ? (
    <a
      href={suggestion.url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 font-medium underline-offset-4 hover:underline"
    >
      Open in Google Calendar
      <ExternalLinkIcon aria-hidden="true" className="size-3.5" />
    </a>
  ) : null
  const buttons = (
    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
      <Button className={THUMB} disabled={busy !== null} onClick={() => void act('add')}>
        {busy === 'add' ? <Spinner aria-hidden="true" /> : <CalendarPlusIcon aria-hidden="true" />}
        Add as time off
      </Button>
      <Button
        variant="ghost"
        className={THUMB}
        disabled={busy !== null}
        onClick={() => void act('dismiss')}
      >
        {busy === 'dismiss' ? <Spinner aria-hidden="true" /> : <XIcon aria-hidden="true" />}
        Dismiss
      </Button>
    </div>
  )

  if (layout === 'row') {
    return (
      <li
        className="flex flex-col gap-2 py-2 first:pt-0 last:pb-0"
        data-testid="time-off-suggestion"
        data-key={suggestion.key}
      >
        <p className="flex flex-col gap-0.5 text-sm">
          <span className="break-words" data-testid="time-off-suggestion-text">
            <Parts text={text} />
          </span>
          {link ? <span className="text-xs text-muted-foreground">{link}</span> : null}
        </p>
        {buttons}
      </li>
    )
  }
  return (
    <section
      aria-labelledby={titleId}
      className="flex flex-col gap-3 rounded-xl border p-4"
      data-testid="time-off-suggestion"
      data-key={suggestion.key}
    >
      <div className="flex items-start gap-2">
        <TreePalmIcon aria-hidden="true" className={cn('mt-0.5 size-4 shrink-0', attention.icon)} />
        <div className="flex min-w-0 flex-col gap-1">
          <h2 id={titleId} className="text-sm font-medium">
            Out of office in your calendar
          </h2>
          <p className="text-sm break-words" data-testid="time-off-suggestion-text">
            <Parts text={text} />
          </p>
          <p className="text-sm text-muted-foreground">
            Add it as time off, and hugo plans around it and helps you tell the people you wait on
            before you leave.
          </p>
          {link ? <p className="text-xs text-muted-foreground">{link}</p> : null}
        </div>
      </div>
      {buttons}
    </section>
  )
}
